import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, of, throwError } from 'rxjs';
import { ApiRouter } from '../../../../../../../../global/routes/api-routes';
import { InspectBundle } from '../../../../../types/inspect-bundle';

type PaymentRedirectResponse = {
  clientReferenceNumber: string;
  orderNumber: string;
  status: string;
  url: string;
};

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  worldPayRequestData = signal<any | undefined>(undefined, {
    equal: (a, b) => JSON.stringify(a ?? {}) === JSON.stringify(b ?? {}),
  });

  worldPayRequest = rxResource({
    params: () => this.worldPayRequestData(),
    stream: ({ params }) => {
      if (!params) return of({} as PaymentRedirectResponse);

      return this.http.post<PaymentRedirectResponse>(ApiRouter.orbit.paymentRedirect(), params);
    },
  });

  putPaymentRequestData = signal<any | undefined>(undefined);

  putPaymentRequest = rxResource({
    params: () => this.putPaymentRequestData(),
    stream: ({ params }) => {
      if (!params) return of({} as any);

      return this.http.post(ApiRouter.orbit.makePayment(), params).pipe(
        catchError((error) => {
          if (error.status === 'PP002') {
            // -> payment isn't needed for the claim, move them on from this page!
            return of({} as any);
          }

          return throwError(() => error);
        })
      );
    },
  });

  constructor(private readonly http: HttpClient) {}

  getWorldpayDataReference(
    data: InspectBundle,
    storedValues: Record<string, any>,
    claimID: string,
    amount: number,
    billingAddress: Record<string, string> | undefined
  ) {
    const productAddress = {
      addressLine1: data?.bundle?.addressLine1,
      addressLine2: data?.bundle?.addressLine2,
      addressLine3: data?.bundle?.addressLine3 === '' ? '.' : data?.bundle?.addressLine3,
      addressLine4: data?.bundle?.addressLine4 === '' ? '.' : data?.bundle?.addressLine4,
      countryCode: data?.bundle?.countryCode,
      postalCode: data?.bundle?.postalCode,
    };

    const newBillingAddress = billingAddress
      ? {
          addressLine1: billingAddress['addressLine1'],
          addressLine2: billingAddress['addressLine2'],
          addressLine3: billingAddress['addressLine3'] === '' ? '.' : billingAddress['addressLine3'],
          addressLine4: '',
          countryCode: data.bundle.countryCode,
          postalCode: billingAddress['postalCode'],
        }
      : productAddress;

    const confirmedCustomerDetails = storedValues['confirm-your-details'];

    const request = {
      address: productAddress,
      billingAddress: newBillingAddress,
      amount: `${amount}`,
      companyCode: data?.bundle?.companyCode,
      currency: 'GBP',
      description: 'Claims',
      ipAddress: '1.1.1.1',
      schemeCode: data?.bundle?.schemeCode,
      referenceNumber: data?.bundle?.reference,
      transactionSource: 'X',
      transactionType: 'W',
      ...(confirmedCustomerDetails?.mobile?.length
        ? { mobTel: confirmedCustomerDetails.mobile.replace('+44', '0') }
        : {}),
      ...(confirmedCustomerDetails?.landline?.length
        ? { homeTel: confirmedCustomerDetails.landline.replace('+44', '0') }
        : {}),
      email: confirmedCustomerDetails?.email,
      name: data?.bundle?.firstName,
      claimID,
    };

    this.worldPayRequestData.set(request);
  }

  updateClaimWithPayment(claimID: string, orderNumber: string, amount: number) {
    const request = {
      amount,
      claimID,
      method: 'CARD_WORLDPAY',
      reference: orderNumber,
      type: 'CLAIM_BOOKING',
    };

    this.putPaymentRequestData.set(request);
  }
}
